import { INavpanelAction, SUBSCRIPTION_STATUS, UploadModelCard } from './../../models/card';
import { FirebaseAuthService } from 'src/app/shared/providers/firebase-auth.service';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { IGroupedCard, IUploadCardEvent, UPLOAD_STATUS } from 'src/app/models/groupedCard';
import { environment } from 'src/environments/environment';
import { SettingsService } from './settings.service';
import { CardsService } from './cards.service';
import { RollbarService, CustomRollbar } from './rollbarlog.service';
import { StopRenderSnackBarComponent } from '../stop-render-snack-bar/stop-render-snack-bar.component';
import { UtilityFunctionsService } from './utility-functions.service';
import { UploadMetadata, UploadTask, getStorage, ref, uploadBytesResumable } from 'firebase/storage';
import { FirebaseError } from 'firebase/app';
import { MobileCommonService } from '../mobile/mobileCommon.service';


export interface IUploadProgress {
  itemId: string;
  progress: number;
  bytesTransfered: number;
  bytesTotal: number;
}

export interface IStorageUploadProgress {
  itemId: string;
  collectionId?: string;
  bytesTransferred: number;
  bytesTotal: number;
}

@Injectable({
  providedIn: 'root'
})
export class UploadFileService {

  public uploadModels: IGroupedCard[] = [];

  public firebaseUploadingItem: IUploadProgress[] = [];
  public itemsTotalProgressSubject: BehaviorSubject<number> = new BehaviorSubject(0);
  private storageUploadItems: IStorageUploadProgress[] = [];

  // web render limit check located in web-upload service
  public mobileRenderLimitTriggerSubject: Subject<void> = new Subject();
  public latestUploadedModelSubscription: Subject<IGroupedCard> = new Subject();

  public uploadErrorSubscription: Subject<void> = new Subject();

  public uploadStatusSubscription: Subject<boolean> = new Subject();

  private lastFileName: string;

  get currentNumberOfUploadingOrRenderingModels(): number {
    return this.uploadModels.filter((groupedCard) => {return groupedCard.uploadStatus.status === UPLOAD_STATUS.INPROGRESS || groupedCard.uploadStatus.status === UPLOAD_STATUS.PROCESSING})?.length;
  }


  constructor(
    @Inject(RollbarService) private rollbar: CustomRollbar,
    public firebaseAuth: FirebaseAuthService,
    public commonMobileService: MobileCommonService,
    public settingService: SettingsService,
    public cardService: CardsService,
    public utilityService: UtilityFunctionsService
    ) {
      this.initializeFirebaseUserSubscription();
    }

  private initializeFirebaseUserSubscription(): void {
    this.firebaseAuth.firebaseUser.subscribe(user => {
      if (user) {
        this.observeFirebaseModelsWithPreprocessing();
      }
    });
  }

  private observeFirebaseModelsWithPreprocessing(): void {
    this.firebaseAuth.getFirebaseModelsWithPreprocessingStatusUpdates().subscribe(dataArray => {
      // console.log('check current processing models in firebase: ', dataArray);
      const sortedArray = dataArray.sort((a, b) => a.modifiedDate - b.modifiedDate);
      sortedArray.forEach(data => this.processDataItem(data));
    });
  }

  private processDataItem(data): void {
    const mockUploadingCard = this.utilityService.convertFirebaseDtoToGroupedCard(data);
    const cardIndex = this.uploadModels.findIndex(card => card.id === mockUploadingCard.id);
    if (cardIndex === -1) {
      this.uploadModels.unshift(mockUploadingCard);
      this.handleModelProcessing(mockUploadingCard);
    }
  }

  // SEND REQUEST FOR UPLOADING (SHOULD BE RENAMED)
  public async requestFileUpload(file: File, collectionId?: string): Promise<void> {

    if (this.shouldRejectUpload()) {
      this.utilityService.uploadServiceNotification('You have reached the limit of uploaded models', 3000);
      return;
    }
    if(this.renderLimitCheckNotPassed()) {
      const isRenderLimitIncreased = await this.commonMobileService.requestIncreasingRenderLimit();
      if(isRenderLimitIncreased) {
        console.log('render limit increased, upload started');
        this.uploadToFirebase(file, collectionId);
      }
      return
    }
    console.log('UPLOAD STARTED');
    this.uploadToFirebase(file, collectionId);
  }

  private shouldRejectUpload(): boolean {
    return this.firebaseAuth.currentUser.subscriptionStatus !== SUBSCRIPTION_STATUS.SUBSCRIBER && this.uploadModels.length > 2;
  }

  private getFileNameWithoutExtension(filename: string): string {
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex <= 0) {
      return filename;
    }
    return filename.slice(0, lastDotIndex);
  }

  public webUploadToFirebase(file: File, custompath: string, collectionToUploadId?: string): void {
    const pathParts = custompath.split('/'); // Splitting the path by '/'
    const filenameWithExtension = pathParts.pop() || ''; // Getting the last part, the filename with extension
    const filenameWithoutExtension = this.getFileNameWithoutExtension(filenameWithExtension);

    const generatedUploadItem: IGroupedCard = this.utilityService.generateMockedUploadItem(filenameWithoutExtension);
    const uploadItemId = generatedUploadItem.id;
    this.uploadModels.unshift(generatedUploadItem);
    this.startFirebaseFileUploadInternal({
      file,
      cardId: uploadItemId,
      collectionId: collectionToUploadId,
   }, custompath);
    console.log('webUploadToFirebase');
  }

  private uploadToFirebase(file: File, collectionId?: string): void {
    const generatedUploadItem: IGroupedCard = this.utilityService.generateMockedUploadItem(this.lastFileName);
    const uploadItemId = generatedUploadItem.id;
    this.uploadModels.unshift(generatedUploadItem);

    const extension = file.name.split('.').pop();
    const customPath = generatedUploadItem.fileName + '/' + generatedUploadItem.fileName + '.' + extension;

    this.startFirebaseFileUploadInternal({
       file,
       cardId: uploadItemId,
       collectionId,
    }, customPath);
  }

  private startFirebaseFileUploadInternal(uploadEvt: IUploadCardEvent, customPath?: string): void {
    this.firebaseAuth.updateNumberOfUploads();

    const uploadItemId = uploadEvt.cardId;
    const file = uploadEvt.file;
    const storageItemId = customPath || uploadItemId;

    const uploadCardItem = this.uploadModels.find((item) => {
      return item.id === uploadItemId
    })

    const uploadDisplayName = uploadCardItem?.fileName || file?.name || uploadItemId;
    console.log('Upload started 0% :', uploadDisplayName);
    // const storageRef = firebase.default.storage().ref().child(`uploaded_models/${this.firebaseAuth.currentUser.uid}/${customPath}/${fileName}`);

    const storage = getStorage();
    const storageRef = ref(storage, `uploaded_models/${this.firebaseAuth.currentUser.uid}/${customPath}`);

    uploadCardItem.uploadStatus.uploadFileRef = storageRef;
    uploadCardItem.uploadStatus.storageItemId = storageItemId;
    const meta: UploadMetadata = {customMetadata: {framesToRender: this.settingService.frameNumberToRender, parentCollectionId: uploadEvt.collectionId}};

    const uploadTask: UploadTask = uploadBytesResumable(storageRef, uploadEvt.file, meta);
    uploadCardItem.uploadStatus.uploadTaskLink = uploadTask;

    uploadCardItem.linkedCollectionId = uploadEvt.collectionId;
    uploadCardItem.uploadStatus.status = UPLOAD_STATUS.INPROGRESS;
    uploadCardItem.uploadStatus.progressMsg = 'Uploading... ';
    uploadCardItem.uploadStatus.spinnerMode = 'indeterminate'; // Set to indeterminate for upload phase

    this.rollbar.info(`Upload ${this.firebaseAuth.currentUser.subscriptionStatus} started: ${this.firebaseAuth.currentUser.uid}/${customPath}`);

    uploadCardItem.uploadStatus.uploadTaskLink.on('state_changed',
      (snapshot) => {
        // Observe state change events such as progress, pause, and resume
        // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;

        this.updateStorageUploadProgress({
          itemId: storageItemId,
          collectionId: uploadEvt.collectionId,
          bytesTransferred: snapshot.bytesTransferred,
          bytesTotal: snapshot.totalBytes,
        });

        this.countTotalUploadProgress({
          itemId: uploadItemId,
          progress,
          bytesTransfered: snapshot.bytesTransferred,
          bytesTotal: snapshot.totalBytes,
        })
        uploadCardItem.uploadStatus.progress = progress
        // Keep spinner as indeterminate during upload
        uploadCardItem.uploadStatus.spinnerMode = 'indeterminate';

        this.uploadStatusSubscription.next(true);
        // console.log('Upload is ' + progress + '% done');

      },
      (error: FirebaseError) => {
        // Reminder
        // https://firebase.google.com/docs/storage/web/handle-errors
        console.log(error);
        this.removeStorageUploadItem(storageItemId);
        this.uploadStatusSubscription.next(true);
        this.removeFromUploadProgress(uploadCardItem);
        uploadCardItem.uploadStatus.status = UPLOAD_STATUS.ERROR
        uploadCardItem.uploadStatus.spinnerMode = 'indeterminate'
        uploadCardItem.uploadStatus.progressMsg = 'Error '
        uploadCardItem.uploadStatus.uploadTaskLink.cancel();
        uploadCardItem.uploadStatus.fileLink = file;

        if(environment.showServicePageAndShowDebugData) {
        this.utilityService.uploadServiceNotification(`Upload error: ${error}`, 10000);
        this.rollbar.error(`Upload error ${this.settingService.getOriginalUidFromLocalstorage()} ${error}`);
        // Your connection is unstable, please try again;

        }
        // Handle unsuccessful uploads
      },
      () => {
        console.log('Upload finished 100% :', uploadDisplayName);
        this.handleModelProcessing(uploadCardItem);
        this.uploadStatusSubscription.next(true);
      }
    );
  }

  private handleModelProcessing(uploadCardItem: IGroupedCard): void {
    const mobileLoadStatus = 'loadStatusMobile';
    const webLoadStatus = 'loadStatusWeb';
    const mobileProgress = 'loadProgressMobile';
    const webProgress = 'loadProgressWeb';
    const mobileWarning = 'warningMobile';
    const webWarning = 'warningWeb';

    console.log('uploadFileName: ', uploadCardItem.fileName)
    const modelDataHandler = async (updateCard: UploadModelCard, unsubscribeHandler) => {
      // Calculate aggregated progress - average of both platforms or use the available one
      let aggregatedProgress = 0; // Default starting progress (0%)
      let mobileProgressValue = 0;
      let webProgressValue = 0;
      let progressCount = 0;

      const hasValidMobileProgress = updateCard?.[mobileProgress] !== undefined &&
                                    updateCard?.[mobileProgress] !== null &&
                                    updateCard?.[mobileProgress] !== '';
      const hasValidWebProgress = updateCard?.[webProgress] !== undefined &&
                                 updateCard?.[webProgress] !== null &&
                                 updateCard?.[webProgress] !== '';

      if(hasValidMobileProgress) {
        mobileProgressValue = parseInt(updateCard[mobileProgress], 10);
        progressCount++;
      }

      if(hasValidWebProgress) {
        webProgressValue = parseInt(updateCard[webProgress], 10);
        progressCount++;
      }

      if(progressCount > 0) {
        const avgProgress = (mobileProgressValue + webProgressValue) / progressCount;
        aggregatedProgress = avgProgress; // Just use the average progress directly
        uploadCardItem.uploadStatus.progress = aggregatedProgress;

        this.countTotalUploadProgress({
          itemId: uploadCardItem.id,
          progress: aggregatedProgress,
          bytesTransfered: 0,
          bytesTotal: 0
        });
      } else if(uploadCardItem.uploadStatus.status === UPLOAD_STATUS.PROCESSING) {
        // If we're in processing mode but don't have progress values yet, keep progress at 0
        uploadCardItem.uploadStatus.progress = 0;

        this.countTotalUploadProgress({
          itemId: uploadCardItem.id,
          progress: 0,
          bytesTransfered: 0,
          bytesTotal: 0
        });
      }

      // Get the aggregated status
      const aggregatedStatus = this.getAggregatedModelStatus(
        updateCard?.[mobileLoadStatus],
        updateCard?.[webLoadStatus]
      );

      // Handle both mobile and web statuses
      const isMobileFinished = updateCard?.[mobileLoadStatus]?.toLowerCase() === 'finished'.toLowerCase();
      const isWebFinished = updateCard?.[webLoadStatus]?.toLowerCase() === 'finished'.toLowerCase();

      // Update previews when available
      if(updateCard?.cardPosterMobile) {
        uploadCardItem.previewImageMobile = updateCard.cardPosterMobile;
      }

      if(updateCard?.cardPosterWeb) {
        uploadCardItem.previewImageWeb = updateCard.cardPosterWeb;
      }

      // Check for subscription errors (same for both platforms)
      if(updateCard?.[mobileWarning]?.toLowerCase() === 'CheckSubscriptionError'.toLowerCase() ||
         updateCard?.[webWarning]?.toLowerCase() === 'CheckSubscriptionError'.toLowerCase()) {
        console.warn('CheckSubscriptionError ', updateCard.id);
        this.rollbar.error(`CheckSubscriptionError ${this.settingService.getOriginalUidFromLocalstorage()}`);
        this.cardService.setSubForStatusSubscription();
      }

      // Check for errors based on aggregated status
      switch(aggregatedStatus.toLowerCase()) {
        case 'parallellimiterror':
          this.rollbar.error(`PARALLEL LIMIT ERROR!!! ${this.settingService.getOriginalUidFromLocalstorage()}`);
          console.error('PARALLEL LIMIT ERROR!!! ')
          this.abortUploadToFirebase(uploadCardItem);
          this.cardService.setSubForStatusSubscription();
          if(environment.production === false) this.utilityService.uploadServiceNotification(`Parallel upload error`, 5000);
          break;

        case 'triallimitreached':
          console.warn('TRIAL LIMIT REACHED!!! ');
          this.rollbar.error(`TRIAL LIMIT REACHED !!! ${this.settingService.getOriginalUidFromLocalstorage()}`);
          this.abortUploadToFirebase(uploadCardItem);
          this.cardService.setSubForStatusSubscription();
          this.uploadErrorSubscription.next();
          this.utilityService.uploadServiceNotification(`Your upload limit reached, please subscribe or contact support`, 5000);
          break;

        case 'usertrialexpirationerror':
          console.warn('TRIAL LIMIT REACHED!!! ');
          this.rollbar.error(`TRIAL LIMIT REACHED !!! ${this.settingService.getOriginalUidFromLocalstorage()}`);
          this.abortUploadToFirebase(uploadCardItem);
          this.cardService.setSubForStatusSubscription();
          this.uploadErrorSubscription.next();
          this.utilityService.uploadServiceNotification(`Your upload limit reached, please subscribe or contact support`, 5000);
          break;

        case 'userexpirationerror':
          console.warn('SUBSCRIBTION EXPIRED!!! ');
          this.rollbar.error(`SUBSCRIBTION EXPIRED !!! ${this.settingService.getOriginalUidFromLocalstorage()}`);
          this.abortUploadToFirebase(uploadCardItem);
          this.cardService.setSubForStatusSubscription();
          this.uploadErrorSubscription.next();
          this.utilityService.uploadServiceNotification(`Your membership expired`, 5000);
          break;

        case 'generalerror':
          const errorPlatform = updateCard?.[mobileLoadStatus]?.toLowerCase() === 'generalError'.toLowerCase() ? 'mobile' : 'web';
          const errorWarning = errorPlatform === 'mobile' ? updateCard?.[mobileWarning] : updateCard?.[webWarning];

          this.utilityService.rollbarErrorThrottlerForModel(errorPlatform + ' generalError', updateCard.id, uploadCardItem.details);
          uploadCardItem.details = `General error (${errorPlatform}): ${errorWarning}`;
          uploadCardItem.uploadStatus.status = UPLOAD_STATUS.ERROR;
          break;

        case 'rendermodelerror':
          const renderErrorPlatform = updateCard?.[mobileLoadStatus]?.toLowerCase() === 'renderModelError'.toLowerCase() ? 'mobile' : 'web';
          const renderErrorWarning = renderErrorPlatform === 'mobile' ? updateCard?.[mobileWarning] : updateCard?.[webWarning];

          this.utilityService.rollbarErrorThrottlerForModel(renderErrorPlatform + ' renderModelError', updateCard.id, uploadCardItem.details);
          uploadCardItem.details = `Render model error (${renderErrorPlatform}): ${renderErrorWarning}`;
          uploadCardItem.uploadStatus.status = UPLOAD_STATUS.ERROR;

          // Update texture-related fields from Firebase data when render error occurs
          const firebaseData = updateCard as any; // Cast to access additional Firebase fields
          if (firebaseData.mappedTextures) {
            uploadCardItem.mappedTextures = firebaseData.mappedTextures;
          }
          if (firebaseData.missingTextures) {
            uploadCardItem.missingTextures = firebaseData.missingTextures;
          }
          if (firebaseData.texturesRootPath) {
            uploadCardItem.texturesRootPath = firebaseData.texturesRootPath;
          }

          console.log('Updated texture data for renderModelError:', {
            mappedTextures: uploadCardItem.mappedTextures,
            missingTextures: uploadCardItem.missingTextures,
            texturesRootPath: uploadCardItem.texturesRootPath
          });
          break;

        case 'userrenderlimittriggered':
          this.rollbar.error(`UserRenderLimitTriggered ${this.settingService.getOriginalUidFromLocalstorage()}`);
          this.abortUploadToFirebase(uploadCardItem);
          this.cardService.setSubForStatusSubscription();
          this.uploadErrorSubscription.next();
          this.utilityService.uploadServiceNotification(`Your render limit reached, please increase limit or contact support`, 5000);
          break;

        case 'finished':
          uploadCardItem.title = this.utilityService.removeTimestampFromString(uploadCardItem.title);
          uploadCardItem.uploadStatus.status = UPLOAD_STATUS.UPLOADED
          uploadCardItem.uploadStatus.progressMsg = 'Uploaded';

          // Use whichever preview is available
          if(isMobileFinished && updateCard.cardContentMobile) {
            uploadCardItem.previewVideoMobile = updateCard.cardContentMobile;
          }

          if(isWebFinished && updateCard.cardContentWeb) {
            uploadCardItem.previewVideoWeb = updateCard.cardContentWeb;
          }

          uploadCardItem.details = updateCard.cardSharingId ? updateCard.cardSharingId : (updateCard as any)['shareID'];

          const modelFullyRendered = isMobileFinished && isWebFinished;

          if (modelFullyRendered) {
            console.log(`[UploadFileService] Model ${uploadCardItem.id} fully ready, adding to list ✅`);
            this.latestUploadedModelSubscription.next(uploadCardItem);
          }

          const uploadingCardsNow = this.uploadModels.filter((groupedCard) => {
            return groupedCard.uploadStatus.status === UPLOAD_STATUS.INPROGRESS ||
                   groupedCard.uploadStatus.status === UPLOAD_STATUS.PROCESSING;
          });

          if(uploadingCardsNow.length === 0) {
            console.log('reset total progress');
            this.firebaseUploadingItem = [];
            this.itemsTotalProgressSubject.next(0);
          }

          // Unsubscribe if both platforms are finished, otherwise keep listening
          if(isMobileFinished && isWebFinished) {
            (await (unsubscribeHandler))();
          }
          break;
      }
    }

    uploadCardItem.uploadStatus.status = UPLOAD_STATUS.PROCESSING
    uploadCardItem.uploadStatus.progressMsg = 'Processing...'
    uploadCardItem.uploadStatus.spinnerMode = 'determinate'; // Set to determinate for processing phase
    uploadCardItem.uploadStatus.progress = 0; // Reset progress to 0 when starting processing

    // Reset total progress in case there are no other items being processed
    this.countTotalUploadProgress({
      itemId: uploadCardItem.id,
      progress: 0,
      bytesTransfered: 0,
      bytesTotal: 0
    });

    console.log('modelProcessing - getFirebaseModelsData ', uploadCardItem);

    this.firebaseAuth.getFirebaseModelsData(modelDataHandler.bind(this), uploadCardItem);
  }




  public abortUploadToFirebase(cardSource: IGroupedCard): void {
    cardSource?.uploadStatus?.uploadTaskLink?.cancel();
    if (cardSource?.uploadStatus?.storageItemId) {
      this.removeStorageUploadItem(cardSource.uploadStatus.storageItemId);
    }
    const cardToDelIndex = this.uploadModels.findIndex((el) => {
      return el.id === cardSource.id
    })
    this.uploadModels.splice(cardToDelIndex, 1);
    this.removeFromUploadProgress(cardSource);
    this.firebaseAuth.deleteDocumentsByIds([cardSource.id]);
    this.firebaseAuth.decreaseNumberOfUploads();
  }

  public abortCardRender(cardSource: IGroupedCard): void {
    this.cardService.stopRenderPostRequest(cardSource.id).then((res) => {

        const cardToDelIndex = this.uploadModels.findIndex((el) => {
          return el.id === cardSource.id
        })
        this.uploadModels.splice(cardToDelIndex, 1);
        this.removeFromUploadProgress(cardSource);
        this.firebaseAuth.deleteDocumentsByIds([cardSource.id]);
        this.firebaseAuth.decreaseNumberOfUploads();


    }).catch((err) => {
      console.error(err);
      this.firebaseAuth.deleteDocumentsByIds([cardSource.id])
    });

  }

  private countTotalUploadProgress(uploadItem?: IUploadProgress): void {
    if (uploadItem) {
      const currentUploadItemIndex = this.firebaseUploadingItem.findIndex((item) => {
        return uploadItem.itemId === item.itemId
      });
      if (currentUploadItemIndex >= 0) {
        this.firebaseUploadingItem.splice(currentUploadItemIndex, 1, uploadItem);
      } else {
        this.firebaseUploadingItem.push(uploadItem);
      }
    }

    const uploadLength = this.firebaseUploadingItem.length;
    let totalProgress = 0;
    if (uploadLength > 0) {
      totalProgress = this.firebaseUploadingItem.reduce((prev, current) => {
        // Ensure progress is a finite number, defaulting to 0 if invalid.
        const safeProgress = Number.isFinite(current.progress) ? current.progress : 0;
        return prev + safeProgress;
      }, 0) / uploadLength;
    }
    this.itemsTotalProgressSubject.next(totalProgress);
    // console.log('uploadingItemsTotalProgress:', totalProgress);
  }

  private removeFromUploadProgress(card: IGroupedCard): void {
    const currentUploadItemIndex = this.firebaseUploadingItem.findIndex((item)=>{return card.id === item.itemId});
    this.firebaseUploadingItem.splice(currentUploadItemIndex, 1);
    this.countTotalUploadProgress();
  }

  public updateStorageUploadProgress(uploadItem: IStorageUploadProgress): void {
    const currentUploadItemIndex = this.storageUploadItems.findIndex((item) => {
      return uploadItem.itemId === item.itemId;
    });
    if (currentUploadItemIndex >= 0) {
      this.storageUploadItems.splice(currentUploadItemIndex, 1, uploadItem);
    } else {
      this.storageUploadItems.push(uploadItem);
    }
    this.cleanupStorageUploadsIfComplete(uploadItem.collectionId);
  }

  public removeStorageUploadItem(itemId: string): void {
    const currentUploadItemIndex = this.storageUploadItems.findIndex((item) => {
      return itemId === item.itemId;
    });
    if (currentUploadItemIndex >= 0) {
      const collectionId = this.storageUploadItems[currentUploadItemIndex].collectionId;
      this.storageUploadItems.splice(currentUploadItemIndex, 1);
      this.cleanupStorageUploadsIfComplete(collectionId);
    }
  }

  public getStorageUploadStatusForCollection(collectionId?: string | null): { progress: number; remainingBytes: number } | null {
    const items = this.storageUploadItems.filter((item) => {
      return this.isStorageItemInCollection(item, collectionId);
    });

    if (items.length === 0) {
      return null;
    }

    const totalBytes = items.reduce((sum, item) => {
      return sum + (Number.isFinite(item.bytesTotal) ? item.bytesTotal : 0);
    }, 0);
    if (totalBytes <= 0) {
      return null;
    }

    const transferredBytes = items.reduce((sum, item) => {
      return sum + (Number.isFinite(item.bytesTransferred) ? item.bytesTransferred : 0);
    }, 0);
    const progress = (transferredBytes / totalBytes) * 100;
    const clampedProgress = Math.min(100, Math.max(0, progress));
    const remainingBytes = Math.max(0, totalBytes - transferredBytes);
    return { progress: Math.round(clampedProgress), remainingBytes };
  }

  public hasActiveStorageUploadsForCollection(collectionId?: string | null): boolean {
    return this.storageUploadItems.some((item) => {
      return this.isStorageItemInCollection(item, collectionId) && this.isStorageUploadItemActive(item);
    });
  }

  public getStorageUploadProgressForCollection(collectionId?: string | null): number | null {
    const status = this.getStorageUploadStatusForCollection(collectionId);
    return status ? status.progress : null;
  }

  private cleanupStorageUploadsIfComplete(collectionId?: string | null): void {
    const items = this.storageUploadItems.filter((item) => {
      return this.isStorageItemInCollection(item, collectionId);
    });
    if (items.length === 0) {
      return;
    }
    const hasActiveUploads = items.some((item) => {
      return this.isStorageUploadItemActive(item);
    });
    if (hasActiveUploads) {
      return;
    }
    this.storageUploadItems = this.storageUploadItems.filter((item) => {
      return !this.isStorageItemInCollection(item, collectionId);
    });
  }

  private isStorageUploadItemActive(item: IStorageUploadProgress): boolean {
    const total = Number.isFinite(item.bytesTotal) ? item.bytesTotal : 0;
    const transferred = Number.isFinite(item.bytesTransferred) ? item.bytesTransferred : 0;
    return total > 0 && transferred < total;
  }

  private isStorageItemInCollection(item: IStorageUploadProgress, collectionId?: string | null): boolean {
    const hasCollectionId = collectionId !== undefined && collectionId !== null && collectionId !== '';
    if (!hasCollectionId) {
      return item.collectionId === undefined || item.collectionId === null || item.collectionId === '';
    }
    return item.collectionId === collectionId;
  }

  public getUploadCountsForCollection(collectionId?: string | null): { uploading: number; rendering: number; errors: number; total: number } {
    let uploading = 0;
    let rendering = 0;
    let errors = 0;

    this.uploadModels.forEach((model) => {
      if (!this.isUploadModelInCollection(model, collectionId)) {
        return;
      }
      const status = model.uploadStatus?.status;
      if (status === UPLOAD_STATUS.INPROGRESS) {
        uploading += 1;
      } else if (status === UPLOAD_STATUS.PROCESSING) {
        rendering += 1;
      } else if (status === UPLOAD_STATUS.ERROR) {
        errors += 1;
      }
    });

    return { uploading, rendering, errors, total: uploading + rendering };
  }

  private isUploadModelInCollection(model: IGroupedCard, collectionId?: string | null): boolean {
    const hasCollectionId = collectionId !== undefined && collectionId !== null && collectionId !== '';
    if (!hasCollectionId) {
      return model.linkedCollectionId === undefined || model.linkedCollectionId === null || model.linkedCollectionId === '';
    }
    return model.linkedCollectionId === collectionId;
  }


  // UPLOAD CARD HANDLERS

  public async stopCardUpload(cardSource: IGroupedCard): Promise<void> {
    if (cardSource.uploadStatus?.uploadTaskLink) {
      // TODO: RETURN TO QUESTION ABOUT HANDLING STOPPED CARDS ON BACKEND, WHEN WE WILL GET PERSONAL LIBRARY FOR EACH USER;

      cardSource.uploadStatus.status = UPLOAD_STATUS.STOPPED;
      cardSource.uploadStatus.uploadTaskLink.pause();
      const isUploadResumed = await this.showStopUploadCardSnackBarDialog(cardSource);
      if (isUploadResumed) {
         cardSource.uploadStatus.status = UPLOAD_STATUS.INPROGRESS;
        cardSource.uploadStatus.uploadTaskLink.resume();
      } else {
        this.abortUploadToFirebase(cardSource);
      }
    }
  }

  public async stopCardRender(cardSource: IGroupedCard): Promise<void> {
    console.log('stopCardRender TRIGGERED: ', cardSource);

    const isUploadResumed = await this.showStopRenderCardSnackBarDialog(cardSource);
    if (isUploadResumed) {

    } else {
      console.log(`stopCardRender ${cardSource} ABORTED`)
      this.abortCardRender(cardSource);
    }
  }

  public retryUploadToFirebase(retryUploadEvent: IUploadCardEvent): void {
    this.startFirebaseFileUploadInternal(retryUploadEvent);
  }

  public retryCardUpload(cardSource: IGroupedCard) {
    if(cardSource.uploadStatus?.uploadTaskLink)  {
      const trigeredEvent: IUploadCardEvent = {
        file: cardSource.uploadStatus.fileLink,
        cardId: cardSource.id
      }
      this.retryUploadToFirebase(trigeredEvent)
    }
  }

  public showStopUploadCardSnackBarDialog(cardToStop: IGroupedCard): Promise<boolean> {
    return new Promise((resolve, reject) => {

      const snack = this.utilityService.uploadServiceNotification(`Uploading stopped`, 4000, 'Cancel');

      snack.onAction().subscribe(()=>{
        resolve(true)
      })
      setTimeout(() => {
        resolve(false);
      }, 4500);
    });
  }

  public showStopRenderCardSnackBarDialog(cardToStop: IGroupedCard): Promise<boolean> {
    return new Promise((resolve, reject) => {

      const snackRef = this.utilityService.uploadServiceNotificationFromComponent(
        'Rendering stopped',
        4000,
        'Cancel',
        StopRenderSnackBarComponent,
        { modelId: cardToStop.id }
      );

      snackRef.instance.actionTaken.subscribe((action) => {
        if (action) {
          resolve(false);
        }
      });

      snackRef.instance.cancelled.subscribe(() => {
        snackRef.dismiss(); // Dismiss the snackbar when "Cancel" is clicked
        resolve(true); // Resolve the promise as false
      });
    });
  }

 // SHOULD BE UPDATED CODE BASE BELOW :

  async pickAndUploadFileNativeIos(action: INavpanelAction) {
    this.processFileUpload(action);
  }

  async pickAndUploadFileNative() {
    this.processFileUpload();
  }

  private async processFileUpload(action?: INavpanelAction) {
    try {
      const file = await this.utilityService.pickFile();
      if (file) {
        console.log('webAPIFile:', file);
        const resultFilename = file.name.slice(0, file.name.lastIndexOf('.'));
        this.setLastFileToUploadName(resultFilename);
        this.requestFileUpload(file, action?.optionalData);
      }
    } catch (error) {
      console.error('Error picking and uploading file:', error);
      this.utilityService.showErrorSnackBar('Failed to pick and upload file. Please try again.', 3000);
    }
  }

  // UTILITY

  public setLastFileToUploadName(name: string): void {
    const filePostfix = new Date().getTime().toString() + environment.uploadFilePostfix;
    this.lastFileName = name.replace(/\s+/g, '') + filePostfix;
  }

  public getLastFileName(): string {
    return this.lastFileName;
  }

  private renderLimitCheckNotPassed(): boolean {
    return this.firebaseAuth.currentUser.subscriptionStatus === SUBSCRIPTION_STATUS.SUBSCRIBER && (this.firebaseAuth.currentUser.renderModelsPerCycle + this.currentNumberOfUploadingOrRenderingModels) >= this.firebaseAuth.currentUser.renderLimit;
  }

  /**
   * Determines the aggregated status of a model across both platforms.
   * A model is considered "finished" only when both platforms are finished.
   * @param mobileStatus The rendering status on mobile
   * @param webStatus The rendering status on web
   * @returns The aggregated status
   */
  private getAggregatedModelStatus(mobileStatus: string, webStatus: string): string {
    const normalizedMobileStatus = mobileStatus?.toLowerCase();
    const normalizedWebStatus = webStatus?.toLowerCase();

    // Error statuses take priority
    const errorStatuses = ['renderModelError', 'generalError', 'ParallelLimitError',
                           'TrialLimitReached', 'UserTrialExpirationError', 'UserExpirationError',
                           'UserRenderLimitTriggered'];

    // Check if either platform has an error
    for (const errorStatus of errorStatuses) {
      const normalizedErrorStatus = errorStatus.toLowerCase();
      if (normalizedMobileStatus === normalizedErrorStatus ||
          normalizedWebStatus === normalizedErrorStatus) {
        return errorStatus;
      }
    }

    const isMobileFinished = normalizedMobileStatus === 'finished';
    const isWebFinished = normalizedWebStatus === 'finished';

    if (isMobileFinished && isWebFinished) {
      return 'finished';
    }

    if (isMobileFinished && !isWebFinished) {
      return webStatus || 'processStarted';
    }

    if (isWebFinished && !isMobileFinished) {
      return mobileStatus || 'processStarted';
    }

    // Otherwise use whichever status is available
    return webStatus || mobileStatus || 'processStarted';
  }

}

  // async pickAndUploadFileNativeIos (action: INavpanelAction) {
  //   const file = await this.pickFileNative()
  //   console.log('file selected', file.name);
  //   console.log('file selected', file.size);

  //   const filename = file.name;
  //   const resultFilename = filename.slice(0, filename.lastIndexOf('.'));
  //   this.setLastFileToUploadName(resultFilename);
  //   this.requestFileUpload(file, action.optionalData, undefined);

  // }

  // async pickFileNative(): Promise<File> {
  //   try {
  //     // Call file picker with extension filtering
  //     const result = await FilePicker.pickFiles({
  //       multiple: false,
  //       types: ['model/vnd.pixar.usdz', 'model/vnd.usdz+zip'],
  //       readData: true,

  //     });

  //     if (result && result.files && result.files.length > 0) {
  //       const file = result.files[0];
  //       const webAPIFile = this.utilityService.createWebAPIFile(file);

  //       if (webAPIFile) {
  //         return webAPIFile;
  //       } else {
  //         console.error('Failed to create Web API File object');
  //       }
  //     }
  //   } catch (error) {
  //     console.error('Error picking file:', error);
  //   }
  // }

  // async pickAndUploadFileNative() {
  //   try {
  //     // Call file picker with extension filtering
  //     const result = await FilePicker.pickFiles({
  //       multiple: false,
  //       types: ['model/vnd.pixar.usdz', 'model/vnd.usdz+zip'],
  //       readData: true,

  //     });

  //     if (result && result.files && result.files.length > 0) {
  //       const file = result.files[0];
  //       const webAPIFile = this.utilityService.createWebAPIFile(file);

  //       if (webAPIFile) {
  //         // Upload file to Firebase
  //         // await this.uploadFileToFirebase(webAPIFile);
  //         console.log('webAPIFile:', webAPIFile);
  //         const filename = webAPIFile.name;
  //         const resultFilename = filename.slice(0, filename.lastIndexOf('.'));
  //         this.setLastFileToUploadName(resultFilename);
  //         this.requestFileUpload(webAPIFile, undefined, resultFilename);
  //       } else {
  //         console.error('Failed to create Web API File object');
  //       }
  //     }
  //   } catch (error) {
  //     console.error('Error picking file:', error);
  //   }
  // }




// public _setErrorOnUploadCard(card: IGroupedCard): void {
//   this.removeFromUploadProgress(card);
//   const uploadCardItem = this.uploadModels.find((item) => {
//     return item.id === card.id
//   })
//   uploadCardItem.uploadStatus.status = UPLOAD_STATUS.ERROR
//   uploadCardItem.uploadStatus.spinnerMode = 'indeterminate'
//   uploadCardItem.uploadStatus.progressMsg = 'Error '
//   uploadCardItem.uploadStatus.uploadTaskLink.cancel();
// }











// public isUploadingOrProcessingNow3inRow(): boolean {
//   return this.uploadModels.filter(item => item?.uploadStatus?.status === UPLOAD_STATUS.INPROGRESS || item?.uploadStatus?.status === UPLOAD_STATUS.PROCESSING).length > 2;
// }

// public isUploadingOrProcessingNow(): boolean {
//   return this.uploadModels.filter(item => item?.uploadStatus?.status === UPLOAD_STATUS.INPROGRESS || item?.uploadStatus?.status === UPLOAD_STATUS.PROCESSING).length > 0;
// }

// private formatBytes(bytes, decimals = 2) {
//   if (bytes === 0) return '0 Bytes';

//   const k = 1024;
//   const dm = decimals < 0 ? 0 : decimals;
//   const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

//   const i = Math.floor(Math.log(bytes) / Math.log(k));

//   return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
// }

  // if(this.currentProcessingModelsChecked === false) {
  //   this.currentProcessingModelsChecked = true;
  //   const processModels = await firebaseAuth.getDocumentsWithLoadStatusNotFinished();
  //   if(processModels.length > 0) {
  //     processModels.forEach((modelInProcessing) => {
  //       const mockItem = this.generateMockedUploadItem();
  //       mockItem.fileName = modelInProcessing;
  //       mockItem.id = modelInProcessing;
  //       this.uploadModels.unshift(mockItem);
  //       this.handleModelProcessing(mockItem);
  //     })
  //   }
  // }

 // public getIsUploadDisabled(): boolean {
    // if(this.firebaseAuth.currentUser.subscriptionStatus === SUBSCRIPTION_STATUS.SUBSCRIBER && this.uploadModels.length > 2)
    // {
    //   return this.isUploadingOrProcessingNow3inRow();
    // }
    // else {
    //   return this.isUploadingOrProcessingNow();
    // }
  // }

  // async uploadFileToFirebase(file: globalThis.File) {
  //   try {
  //     // Create reference to file in Firebase Storage
  //     const storageRef = await this.firebaseAuth.getStorageRef();
  //     const fileRef = storageRef.child(`your-folder/${file.name}`);

  //     // Upload File to Firebase Storage
  //     const uploadTaskSnapshot = await fileRef.put(file);
  //     const downloadUrl = await uploadTaskSnapshot.ref.getDownloadURL();

  //     console.log('File uploaded:', downloadUrl);
  //   } catch (error) {
  //     console.error('Error uploading file:', error);
  //   }
  // }

    // private getProcessingModelsFromLocalStorage(): string[] {
  //   let currentProcessingModels: string[] = JSON.parse(localStorage.getItem(LOCALSTORAGE_VAR.MODELS_CURRENT_PROCESSING));
  //   if (currentProcessingModels === undefined || currentProcessingModels === null) {
  //     localStorage.setItem(LOCALSTORAGE_VAR.MODELS_CURRENT_PROCESSING, JSON.stringify([]));
  //     currentProcessingModels = [];
  //   }
  //   return currentProcessingModels
  // }

  // private saveModelInProssingList(modelFileName: string): void {
  //   const getProcessingModelsFromLocalStorage = this.getProcessingModelsFromLocalStorage();
  //   if( getProcessingModelsFromLocalStorage.findIndex(savedModel => savedModel === modelFileName) === -1) {
  //     getProcessingModelsFromLocalStorage.push(modelFileName);
  //     localStorage.setItem(LOCALSTORAGE_VAR.MODELS_CURRENT_PROCESSING, JSON.stringify(getProcessingModelsFromLocalStorage));
  //     console.log('model saved in localstorage processing list: ', JSON.parse(localStorage.getItem(LOCALSTORAGE_VAR.MODELS_CURRENT_PROCESSING)))
  //   }
  //   else {
  //     console.error('something wrong with saving processing model in localstorage')
  //   }
  // }

  // private removeModelInProcessList(modelFileName: string): void {
  //   const getProcessingModelsFromLocalStorage = this.getProcessingModelsFromLocalStorage();
  //   const modelToRemoveIndex = getProcessingModelsFromLocalStorage.findIndex(savedModel => savedModel === modelFileName);
  //   if(modelToRemoveIndex > -1) {
  //     getProcessingModelsFromLocalStorage.splice(modelToRemoveIndex, 1);
  //     localStorage.setItem(LOCALSTORAGE_VAR.MODELS_CURRENT_PROCESSING, JSON.stringify(getProcessingModelsFromLocalStorage));
  //     console.log('model removed from localstorage processing list: ', JSON.parse(localStorage.getItem(LOCALSTORAGE_VAR.MODELS_CURRENT_PROCESSING)))
  //   }
  //   else {
  //     console.error('something wrong with removing processing model from localstorage');
  //     console.log('modelToRemove: ', modelFileName);
  //     console.log(this.getProcessingModelsFromLocalStorage())
  //   }
  // }
